// src/components/FormLists/FormListsLabSidePanelList.jsx

import React, { memo } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
} from '@mui/material';

import FormListsGroupHeaderContent from './FormListsGroupHeaderContent';

import {
  getGroupId,
} from '../Spreadsheet/groupedSpreadsheetUtils';

/**
 * FormListsLabSidePanelList
 * -----------------------------------------------------------------------------
 * Minimal Form Lists row item for SpreadsheetWorkspaceSidePanel list mode.
 *
 * PURPOSE
 *  • Match the SmartLists sidepanel item pattern.
 *  • Show only:
 *      - field type icon
 *      - field label
 *      - optional SmartList/org-wide indicator
 *  • Let SpreadsheetWorkspaceSidePanel own drag/drop behavior.
 *
 * IMPORTANT
 *  • This component does NOT render settings buttons.
 *  • This component does NOT render reorder arrows.
 *  • This component does NOT own drag/drop.
 *  • This component does NOT write any data.
 *  • Clicking the row raises intent to open the existing FieldSettings panel.
 */

const FormListsLabSidePanelList = memo(({
  group,
  selected = false,
  onClick,
}) => {
  const groupId = getGroupId(group);

  return (
    <Box
      role="button"
      tabIndex={0}
      data-group-id={groupId}
      onClick={() => {
        if (typeof onClick === 'function') {
          onClick(group);
        }
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();

          if (typeof onClick === 'function') {
            onClick(group);
          }
        }
      }}
      sx={{
        width: '100%',
        minWidth: 0,
        m: 0,
        px: 1.25,
        py: 0.75,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        boxSizing: 'border-box',
        cursor: 'grab',
        backgroundColor: selected
          ? 'rgba(25, 118, 210, 0.08)'
          : 'transparent',
        '&:hover': {
          backgroundColor: selected
            ? 'rgba(25, 118, 210, 0.10)'
            : 'rgba(0, 0, 0, 0.04)',
        },
        '&:active': {
          cursor: 'grabbing',
        },
      }}
    >
      <Box
        sx={{
          flex: '1 1 auto',
          minWidth: 0,
          maxWidth: '100%',
          display: 'flex',
          alignItems: 'center',
          overflow: 'hidden',
          color: 'text.primary',
        }}
      >
        <FormListsGroupHeaderContent
          group={group}
          variant="sidepanel"
        />
      </Box>
    </Box>
  );
});

FormListsLabSidePanelList.displayName = 'FormListsLabSidePanelList';

FormListsLabSidePanelList.propTypes = {
  group: PropTypes.object.isRequired,
  selected: PropTypes.bool,
  onClick: PropTypes.func,
};

export default FormListsLabSidePanelList;